/**
 * useFloating — positions a floating panel (tooltip, popover/menu, date-picker calendar)
 * as a `position: fixed` element anchored to a trigger, so it is never clipped by an
 * ancestor's `overflow: hidden` (every `.argus-card` has it) and never cut off by the
 * viewport edge (PO ruling 2026-10-05, "border aware tooltips also" — see floating.ts for
 * the flip/clamp rules).
 *
 * The caller still owns the panel's JSX (role, aria-*, children, its own focus/Escape/
 * outside-click wiring) and portals it into `document.body` itself with `createPortal`;
 * this hook only supplies WHERE to put it: a ref for the panel element, the `position:
 * fixed` style (top/left in viewport coordinates, plus a z-index override — see below),
 * and the placement actually used after any flip, for `data-placement` (so CSS can orient
 * a transform-origin, or an arrow if one is added later).
 *
 * Measures after mount with `useLayoutEffect` (so there is no one-frame flash in the wrong
 * spot), then re-measures on any ancestor `scroll` (capture phase — `scroll` does not
 * bubble), window `resize`, and the panel's own size changing (`ResizeObserver` on the
 * panel — e.g. the firm switcher's menu, whose height changes once its firms query
 * resolves).
 *
 * z-index: the panel's base layer (`.argus-floating`, `--z-popover`) sits above the shell
 * chrome but below the modal overlay, so a modal opening over a stale/forgotten-open
 * popover still covers it (see primitives.css for the full scale: topnav 40, mobile
 * sidebar 60 / its backdrop 55, modal overlay 100, `--z-popover` 80). But a popover whose
 * trigger lives INSIDE an open `.argus-modal` must render above that modal, not be hidden
 * under the dialog it belongs to — so this hook checks the anchor's ancestors for an open
 * `.argus-modal` and, when found, overrides the z-index to `--z-popover-in-modal` (110,
 * just above the modal's 100) via an inline style.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';

import { positionFloating, type FloatingPlacement } from './floating.ts';

const MODAL_SELECTOR = '.argus-modal';
/** Parked off-screen until the first measurement, so there is nothing to flash at (0, 0). */
const UNMEASURED = -9999;

export interface UseFloatingOptions {
  /** Gap kept between the anchor and the panel along the main axis. Default 6. */
  offset?: number | undefined;
  /** Minimum gutter kept between the panel and every viewport edge. Default 8. */
  margin?: number | undefined;
}

export interface UseFloatingResult<T extends HTMLElement = HTMLElement> {
  /** Attach to the floating panel element (the one that gets portaled). */
  panelRef: RefObject<T>;
  /** Spread onto the panel: `position: fixed`, the computed `top`/`left`, and the
   *  over-modal z-index override when it applies. */
  style: CSSProperties;
  /** The resolved placement, after any flip — set it as `data-placement` on the panel. */
  placement: FloatingPlacement;
}

export function useFloating<T extends HTMLElement = HTMLElement>(
  anchorRef: RefObject<HTMLElement>,
  open: boolean,
  placement: FloatingPlacement,
  options?: UseFloatingOptions,
): UseFloatingResult<T> {
  const panelRef = useRef<T>(null);
  const offset = options?.offset;
  const margin = options?.margin;

  const [state, setState] = useState<{
    top: number;
    left: number;
    placement: FloatingPlacement;
    overModal: boolean;
  }>({ top: UNMEASURED, left: UNMEASURED, placement, overModal: false });

  const recompute = useCallback(() => {
    const anchorEl = anchorRef.current;
    const panelEl = panelRef.current;
    if (!anchorEl || !panelEl) return;
    const result = positionFloating({
      anchor: anchorEl.getBoundingClientRect(),
      panel: { width: panelEl.offsetWidth, height: panelEl.offsetHeight },
      placement,
      offset,
      margin,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
    setState({
      top: result.top,
      left: result.left,
      placement: result.placement,
      overModal: anchorEl.closest(MODAL_SELECTOR) !== null,
    });
  }, [anchorRef, placement, offset, margin]);

  // Measure synchronously before paint so the panel never flashes at (0, 0) / its previous
  // spot — this runs after the panel has mounted (off-screen) and been laid out.
  useLayoutEffect(() => {
    if (open) recompute();
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && panelRef.current) {
      observer = new ResizeObserver(recompute);
      observer.observe(panelRef.current);
    }
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
      observer?.disconnect();
    };
  }, [open, recompute]);

  const style: CSSProperties = {
    position: 'fixed',
    top: state.top,
    left: state.left,
    ...(state.overModal ? { zIndex: 'var(--z-popover-in-modal, 110)' } : {}),
  };

  return { panelRef, style, placement: state.placement };
}
